import * as React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

import { cn } from '../../lib/utils';

// Lightweight modal with overlay, ESC-to-close, click-outside, and scroll lock.
// Deliberately dependency-free (no Radix dialog) to keep the template lean.
function Dialog({ open, onClose, children, className }) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      // Escape closes the dialog — unless an inner menu/popover (Radix marks
      // the event as handled, and its layer is still in the DOM) took it first,
      // so closing a dropdown never closes the whole modal.
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[role="menu"], [data-radix-popper-content-wrapper]')) return;
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:items-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cn('relative my-8 w-full max-w-lg overflow-hidden rounded-lg border bg-card text-card-foreground shadow-xl', className)}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

function DialogHeader({ title, description, onClose }) {
  return (
    <div className="border-b border-border bg-muted/40 px-4 py-2.5">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          </span>
          <h2 className="ml-1 text-sm font-medium text-muted-foreground">
            <span className="text-primary">$</span> {title}
          </h2>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 rounded-md p-1 text-muted-foreground transition hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      {description ? <p className="mt-2 text-sm text-muted-foreground">{description}</p> : null}
    </div>
  );
}

function DialogBody({ className, ...props }) {
  return <div className={cn('px-6 py-4', className)} {...props} />;
}

function DialogFooter({ className, ...props }) {
  return <div className={cn('flex items-center justify-end gap-2 border-t border-border px-6 py-4', className)} {...props} />;
}

export { Dialog, DialogHeader, DialogBody, DialogFooter };
